import React, { useState } from 'react';
import { Checkbox, Input, Dropdown, Icon, Popup, Button } from 'semantic-ui-react';
import _ from 'lodash';
import { post } from '../../../util/api';
import { ParamField, JsonInput, formatDuration } from './fields';

// cronOptions editörü. Şema live-api'den gelir (CronService getCronSchema); bilinmeyen tip ve alanlar silinmez, uyarıyla gösterilir.
// Kural: { active, dev?, priority, conditions: [{type, params}], actions: [{type, params}] }
// Çalışma: aynı öncelikteki kurallar paralel, büyük öncelik önce. Kayıt üst sayfadaki kaydet çubuğundan.

const TEMPLATES = [
	{ key: 'empty', text: 'Boş kural', rule: { conditions: [], actions: [] } },
	{
		key: 'bot-forward',
		text: 'Botta yanıtsız kalanı agent beklemeye aktar',
		rule: {
			conditions: [
				{ type: 'state', params: { state: 'open-bot' } },
				{ type: 'no-message', params: { from: 'user', delay: 300 } },
			],
			actions: [{ type: 'forwardConversation', params: { toState: 'forward-waiting' } }],
		},
	},
	{
		key: 'non-taken-close',
		text: 'Agent almamış, güncellenmeyeni kapat',
		rule: {
			conditions: [
				{ type: 'state', params: { state: 'open-non-taken' } },
				{ type: 'no-update', params: { delay: 86400 } },
			],
			actions: [{ type: 'closeConversation', params: { closeAs: 'auto' } }],
		},
	},
	{
		key: 'taken-close',
		text: 'Agent almış, güncellenmeyeni kapat',
		rule: {
			conditions: [
				{ type: 'state', params: { state: 'taken-by-agent' } },
				{ type: 'no-update', params: { delay: 3600 } },
			],
			actions: [{ type: 'closeConversation', params: { closeAs: 'auto' } }],
		},
	},
	{
		key: 'wait-message',
		text: 'Agent almış, bekleme mesajı (rastgele)',
		rule: {
			conditions: [
				{ type: 'state', params: { state: 'taken-by-agent' } },
				{ type: 'no-update', params: { delay: 5, delay_max: 120 } },
			],
			actions: [
				{
					type: 'sendMessage',
					params: { random_message: [{ type: 'text', text: 'Kontrol ediyorum, kısa bir süre rica ediyorum.' }], is_last_message_from_user: true },
				},
			],
		},
	},
	{
		key: 'ask-continue',
		text: 'Yanıt gelmezse devam etmek istiyor mu diye sor',
		rule: {
			conditions: [
				{ type: 'state', params: { state: 'taken-by-agent' } },
				{ type: 'no-update', params: { delay: 10, delay_max: 300 } },
			],
			actions: [
				{
					type: 'sendMessage',
					params: { message: { type: 'text', text: 'Sizden yanıt alamıyorum, görüşmeye devam etmek istiyor musunuz?' }, is_last_message_from_user: false },
				},
			],
		},
	},
];

function metaOf(list, type) {
	return _.find(list, { type });
}

function stateLabel(ctx, key) {
	let s = _.find(ctx.schema.states, { key });
	return s ? s.label : key;
}

function describeCondition(c, ctx) {
	let p = c.params || {};
	switch (c.type) {
		case 'state':
			return _.isString(p.state) ? stateLabel(ctx, p.state) : 'özel durum';
		case 'no-message':
			return 'kullanıcıdan ' + formatDuration(p.delay) + ' mesaj yok';
		case 'no-update':
			return formatDuration(p.delay) + (p.delay_max ? '–' + formatDuration(p.delay_max) + ' arası' : '') + ' güncellenmedi';
		case 'tag':
			return 'etiket: ' + ctx.tagName(p.tag);
		case 'no-tag':
			return 'etiket yok: ' + _.map(_.castArray(p.tag || []), ctx.tagName).join(', ');
		default:
			return c.type;
	}
}

function describeAction(a, ctx) {
	let p = a.params || {};
	let meta = metaOf(ctx.schema.actions, a.type);
	let label = meta ? meta.label : a.type;
	switch (a.type) {
		case 'closeConversation':
			return label + (p.closeAs === 'agent' ? ' (agent adına)' : '');
		case 'forwardConversation':
			return label + ': ' + stateLabel(ctx, p.toState) + (p.toGroup ? ' / ' + ctx.groupName(p.toGroup) : '');
		case 'sendMessage':
			if (_.isArray(p.random_message) && p.random_message.length) return label + ' (rastgele ' + p.random_message.length + ')';
			return label + (p.message && p.message.text ? ': "' + _.truncate(p.message.text, { length: 40 }) + '"' : '');
		default:
			return label;
	}
}

export function ruleWarnings(rule, ctx) {
	let w = [];
	let conditions = rule.conditions || [];
	let actions = rule.actions || [];
	if (!conditions.length) w.push('Koşul yok: hesaptaki tüm konuşmalara (kapalılar dahil) uygulanır.');
	else if (!_.some(conditions, { type: 'state' })) w.push('Durum koşulu yok: kapalı konuşmalar da eşleşir.');
	if (!actions.length) w.push('Aksiyon yok.');

	let check = (item, list, kind) => {
		let meta = metaOf(list, item.type);
		if (!meta) return w.push(kind + ' tipi bilinmiyor, çalışmaz: ' + item.type);
		let params = item.params || {};
		_.each(meta.params, (m) => {
			if (m.required && (params[m.key] === undefined || params[m.key] === '')) w.push(meta.label + ': "' + m.label + '" boş.');
		});
		let known = _.map(meta.params, 'key');
		_.each(_.keys(params), (k) => {
			if (known.indexOf(k) !== -1) return;
			if (k[0] === '_') w.push(meta.label + ': "' + k + '" devre dışı bırakılmış alan (okunmuyor).');
			else if (known.indexOf(k + 's') !== -1) w.push(meta.label + ': "' + k + '" okunmuyor, "' + k + 's" olmalı.');
			else w.push(meta.label + ': "' + k + '" alanı kodda okunmuyor.');
		});
		let extra = _.without(_.keys(item), 'type', 'params');
		if (extra.length) w.push(meta.label + ': params dışında alan (yok sayılır): ' + extra.join(', '));
		if (kind === 'Koşul' && item.type === 'state' && _.isString(params.state)) {
			let s = _.find(ctx.schema.states, { key: params.state });
			if (s && s.target) w.push('"' + s.label + '" hedef durumudur, koşulda hiçbir konuşmayla eşleşmez.');
		}
	};
	_.each(conditions, (c) => check(c, ctx.schema.conditions, 'Koşul'));
	_.each(actions, (a) => check(a, ctx.schema.actions, 'Aksiyon'));
	return w;
}

function ItemEditor({ item, list, ctx, onChange, onRemove }) {
	let meta = metaOf(list, item.type);
	let params = item.params || {};
	let known = meta ? _.map(meta.params, 'key') : [];
	let unknownParams = _.omit(params, known);
	let setParam = (key, value) => onChange(_.assign({}, item, { params: _.omitBy(_.assign({}, params, { [key]: value }), _.isUndefined) }));
	let typeOptions = _.map(list, (m) => ({ key: m.type, value: m.type, text: m.label }));
	if (!meta) typeOptions.push({ key: item.type, value: item.type, text: item.type + ' (bilinmiyor)' });

	return (
		<div className="yta-item">
			<div className="yta-item-head">
				<Dropdown
					inline
					search
					value={item.type}
					options={typeOptions}
					style={{ fontWeight: 600 }}
					onChange={(e, d) => {
						let m = metaOf(list, d.value);
						let defaults = {};
						_.each(m && m.params, (p) => {
							if (p.default !== undefined) defaults[p.key] = p.default;
						});
						onChange({ type: d.value, params: defaults });
					}}
				/>
				<span className="yta-field-key" style={{ margin: 0 }}>
					{item.type}
				</span>
				<div style={{ flex: 1 }} />
				<button className="yta-iconbtn" onClick={onRemove} title="Kaldır">
					<Icon name="close" />
				</button>
			</div>
			{meta && meta.params.length ? (
				<div className="yta-params">
					{_.map(meta.params, (m) => (
						<React.Fragment key={m.key}>
							<div className="lbl" title={m.key}>
								{m.label}
								{m.required ? ' *' : ''}
							</div>
							<div>
								<ParamField meta={m} value={params[m.key]} ctx={ctx} onChange={(v) => setParam(m.key, v)} />
								{m.help ? <div className="yta-field-desc">{m.help}</div> : null}
							</div>
						</React.Fragment>
					))}
				</div>
			) : null}
			{!meta || !_.isEmpty(unknownParams) ? (
				<div style={{ marginTop: 10 }}>
					<div className="yta-field-desc">{meta ? 'Diğer alanlar (korunur)' : 'Parametreler (JSON)'}</div>
					<JsonInput
						value={_.isEmpty(unknownParams) ? undefined : unknownParams}
						rows={3}
						onChange={(v) => onChange(_.assign({}, item, { params: _.assign(_.pick(params, known), v || {}) }))}
					/>
				</div>
			) : null}
		</div>
	);
}

function RuleCard({ rule, ctx, expanded, onToggle, onChange, onRemove, onDuplicate }) {
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	let warnings = ruleWarnings(rule, ctx);
	let conditions = rule.conditions || [];
	let actions = rule.actions || [];
	let set = (patch) => onChange(_.omitBy(_.assign({}, rule, patch), _.isUndefined));
	let setList = (key, list) => set({ [key]: list });

	async function runPreview(e) {
		e.stopPropagation();
		setPreviewing(true);
		try {
			let res = await post('/dahi/message/admin/cron/preview', { token: ctx.token, conditions });
			if (!res.data.success) throw new Error(res.data.error);
			setPreview(res.data.result);
		} catch (error) {
			setPreview({ error: error.message });
		}
		setPreviewing(false);
	}
	let stop = (e) => e.stopPropagation();

	return (
		<div className={'yta-rule' + (rule.active ? ' active' : '')}>
			<div className="yta-rule-head" onClick={onToggle}>
				<Icon name={expanded ? 'chevron down' : 'chevron right'} size="small" color="grey" />
				<span onClick={stop}>
					<Checkbox toggle checked={!!rule.active} onChange={(e, d) => set({ active: d.checked })} />
				</span>
				<span className="yta-badge" title="Öncelik">
					P{rule.priority === undefined ? '?' : rule.priority}
				</span>
				{rule.dev ? <span className="yta-badge accent">dev</span> : null}
				<div className="yta-rule-title">
					<span className="when">{_.map(conditions, (c) => describeCondition(c, ctx)).join(' · ') || 'koşulsuz'}</span>
					<span className="arrow">→</span>
					<b>{_.map(actions, (a) => describeAction(a, ctx)).join(', ') || '—'}</b>
				</div>
				{warnings.length ? (
					<Popup
						size="small"
						content={_.map(warnings, (w, i) => (
							<div key={i}>{w}</div>
						))}
						trigger={
							<span className="yta-badge warn">
								<Icon name="warning sign" style={{ margin: 0 }} /> {warnings.length}
							</span>
						}
					/>
				) : null}
				<span onClick={stop} style={{ display: 'flex', gap: 2 }}>
					<Popup
						size="mini"
						content="Bu kural şu an kaç konuşmayı yakalıyor?"
						trigger={
							<button className="yta-iconbtn" onClick={runPreview}>
								<Icon name={previewing ? 'spinner' : 'search'} loading={previewing} />
							</button>
						}
					/>
					<Popup
						size="mini"
						content="Çoğalt"
						trigger={
							<button className="yta-iconbtn" onClick={onDuplicate}>
								<Icon name="copy outline" />
							</button>
						}
					/>
					<Popup
						size="mini"
						content="Sil"
						trigger={
							<button className="yta-iconbtn" onClick={onRemove}>
								<Icon name="trash alternate outline" />
							</button>
						}
					/>
				</span>
			</div>
			{preview ? (
				<div style={{ padding: '0 14px 12px' }}>
					<div className={preview.error ? 'yta-warn-list' : 'yta-info'} style={{ marginBottom: 0 }}>
						<span style={{ float: 'right', cursor: 'pointer' }} onClick={() => setPreview(null)}>
							<Icon name="close" />
						</span>
						{preview.error ? (
							preview.error
						) : (
							<>
								<b>Şu an {preview.count} konuşma eşleşiyor.</b>
								{_.map(preview.sample, (s) => (
									<div key={s._id} style={{ marginTop: 3 }}>
										{_.get(s, 'info.name') || _.get(s, 'info.phone') || s._id} · {_.get(s, 'info.platform')} · {s.updateDate ? new Date(s.updateDate).toLocaleString() : ''}
									</div>
								))}
							</>
						)}
					</div>
				</div>
			) : null}
			{expanded ? (
				<div className="yta-rule-body">
					<div className="yta-toolbar">
						<Input
							size="small"
							type="number"
							label="Öncelik"
							value={rule.priority === undefined ? '' : rule.priority}
							onChange={(e, d) => set({ priority: d.value === '' ? undefined : Number(d.value) })}
							style={{ width: 140 }}
						/>
						<Checkbox toggle label="Sadece geliştirme ortamında (dev)" checked={!!rule.dev} onChange={(e, d) => set({ dev: d.checked || undefined })} />
					</div>
					{warnings.length ? (
						<div className="yta-warn-list">
							{_.map(warnings, (w, i) => (
								<div key={i}>
									<Icon name="warning sign" /> {w}
								</div>
							))}
						</div>
					) : null}
					<div className="yta-cols">
						<div>
							<div className="yta-col-title">Ne zaman (hepsi sağlanınca)</div>
							{_.map(conditions, (c, i) => (
								<ItemEditor
									key={'c' + i}
									item={c}
									list={ctx.schema.conditions}
									ctx={ctx}
									onChange={(v) => setList('conditions', _.map(conditions, (x, j) => (j === i ? v : x)))}
									onRemove={() => setList('conditions', _.filter(conditions, (x, j) => j !== i))}
								/>
							))}
							<button className="yta-add" onClick={() => setList('conditions', conditions.concat([{ type: 'state', params: {} }]))}>
								<Icon name="plus" /> Koşul ekle
							</button>
						</div>
						<div>
							<div className="yta-col-title">Ne yapılsın</div>
							{_.map(actions, (a, i) => (
								<ItemEditor
									key={'a' + i}
									item={a}
									list={ctx.schema.actions}
									ctx={ctx}
									onChange={(v) => setList('actions', _.map(actions, (x, j) => (j === i ? v : x)))}
									onRemove={() => setList('actions', _.filter(actions, (x, j) => j !== i))}
								/>
							))}
							<button className="yta-add" onClick={() => setList('actions', actions.concat([{ type: 'closeConversation', params: { closeAs: 'auto' } }]))}>
								<Icon name="plus" /> Aksiyon ekle
							</button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}

function buildCtx(schema, product) {
	let staticOptions = product.staticOptions || {};
	let tags = _.map(staticOptions.tags, (t) => ({ key: t._id, value: t._id, text: t.title }));
	let groups = _.map(staticOptions.groups, (g) => ({ key: g.id, value: g.id, text: g.label || g.name || g.id }));
	_.each(staticOptions.phone_groups, (g) => groups.push({ key: 'phone_' + g.id, value: 'phone_' + g.id, text: g.label + ' (telefon)' }));
	return {
		schema,
		token: product.token,
		tags,
		groups,
		tagName: (id) => _.get(_.find(tags, { value: id }), 'text', id),
		groupName: (id) => _.get(_.find(groups, { value: id }), 'text', id),
	};
}

// genel arama için: kuralın okunur özeti ve uyarıları
export function ruleSummary(rule, schema, product) {
	let ctx = buildCtx(schema, product);
	return {
		when: _.map(rule.conditions, (c) => describeCondition(c, ctx)).join(' · ') || 'koşulsuz',
		then: _.map(rule.actions, (a) => describeAction(a, ctx)).join(', ') || '—',
		warnings: ruleWarnings(rule, ctx),
	};
}

export default function Automation({ rules, period, onRulesChange, onPeriodChange, schema, product, focusRule }) {
	const [expanded, setExpanded] = useState(focusRule !== undefined && focusRule !== null ? { [focusRule]: true } : {});
	const [raw, setRaw] = useState(false);
	rules = rules || [];
	let ctx = buildCtx(schema, product);

	let periodValue = period || schema.defaultPeriod;
	let activeCount = _.filter(rules, 'active').length;
	let setRules = (next) => onRulesChange(next);

	return (
		<div>
			<div className="yta-info">
				Kurallar her {periodValue} dakikada bir çalışır. Önceliği yüksek olan önce, aynı öncelikliler birlikte çalışır. Yeni kurallar pasif eklenir;
				aktif etmeden önce <Icon name="search" fitted /> ile kaç konuşmayı yakaladığına bakın.
			</div>
			<div className="yta-toolbar">
				<span>Çalışma aralığı</span>
				<Dropdown
					selection
					compact
					value={periodValue}
					options={_.map(_.uniq(schema.periods.concat([periodValue])), (p) => ({
						key: p,
						value: p,
						text: p + ' dk' + (60 % p ? ' (düzensiz)' : '') + (p === schema.defaultPeriod && !period ? ' · varsayılan' : ''),
					}))}
					onChange={(e, d) => onPeriodChange(d.value === schema.defaultPeriod && !period ? undefined : d.value)}
				/>
				<span className="yta-badge">{rules.length} kural</span>
				<span className="yta-badge ok">{activeCount} aktif</span>
				<div className="grow" />
				<Checkbox toggle label="Ham JSON" checked={raw} onChange={(e, d) => setRaw(d.checked)} />
				<Dropdown
					button
					className="icon primary"
					labeled
					icon="plus"
					text="Kural ekle"
					value={null}
					options={_.map(TEMPLATES, (t) => ({ key: t.key, value: t.key, text: t.text }))}
					onChange={(e, d) => {
						let t = _.find(TEMPLATES, { key: d.value });
						setExpanded(_.assign({}, expanded, { [rules.length]: true }));
						setRules(rules.concat([_.assign({ active: false, priority: 1 }, _.cloneDeep(t.rule))]));
					}}
				/>
			</div>

			{raw ? (
				<div className="yta-card">
					<div className="yta-card-body">
						<JsonInput value={rules} rows={24} onChange={(v) => _.isArray(v) && setRules(v)} />
					</div>
				</div>
			) : (
				<>
					{_.map(rules, (rule, i) => (
						<RuleCard
							key={i}
							rule={rule}
							index={i}
							ctx={ctx}
							expanded={!!expanded[i]}
							onToggle={() => setExpanded(_.assign({}, expanded, { [i]: !expanded[i] }))}
							onChange={(r) => setRules(_.map(rules, (x, j) => (j === i ? r : x)))}
							onRemove={() => window.confirm('Kural silinsin mi?') && setRules(_.filter(rules, (x, j) => j !== i))}
							onDuplicate={() => setRules(rules.concat([_.assign(_.cloneDeep(rule), { active: false })]))}
						/>
					))}
					{!rules.length ? (
						<div className="yta-card">
							<div className="yta-empty">
								<Icon name="clock outline" size="big" />
								<div style={{ marginTop: 8 }}>Bu hesapta otomasyon kuralı yok.</div>
								<Button basic size="small" style={{ marginTop: 10 }} onClick={() => setRules([_.assign({ active: false, priority: 1 }, _.cloneDeep(TEMPLATES[1].rule))])}>
									Örnek kuralla başla
								</Button>
							</div>
						</div>
					) : null}
				</>
			)}
		</div>
	);
}
